import {
  Bookmark,
  Circle,
  GlassWater,
  IceCreamCone,
  Minus,
  PackageOpen,
  Plus,
  Salad,
  ShoppingCart,
  Trash2,
  UtensilsCrossed,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { AdminInvitationModal } from "@/components/admin-invitation-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import type { GuestSessionState } from "@/hooks/use-guest-session";
import { useGuestSession } from "@/hooks/use-guest-session";
import { usePublicMenu } from "@/hooks/use-menu";
import { useTheme } from "@/hooks/use-theme";
import { useCart } from "@/lib/cart-store";
import { formatPrice } from "@/lib/format-price";
import { cn } from "@/lib/utils";
import type { MenuSection, PublicMenuProduct, PublicMenuResponse } from "@/types/menu";

const UPLOADTHING_BASE_URL = "https://paj514wbqe.ufs.sh/f";

type SupermarketSubcategory = {
  section: MenuSection;
  products: PublicMenuProduct[];
};

export type SupermarketCategory = {
  section: MenuSection;
  directProducts: PublicMenuProduct[];
  subcategories: SupermarketSubcategory[];
  productCount: number;
};

type MenuContentProps = {
  data: PublicMenuResponse;
  orgSlug?: string;
  guest: GuestSessionState;
  qrToken: string | null;
  canOrder: boolean;
  quantitiesByProductId: Map<string, number>;
  onAdd: (product: PublicMenuProduct) => void;
  onDecrement: (productId: string) => void;
};

type PublicBusinessType = PublicMenuResponse["organization"]["businessType"];

export function groupProductsBySection(products: PublicMenuProduct[]) {
  const map = new Map<string, PublicMenuProduct[]>();
  for (const product of products) {
    if (!product.sectionId) {
      continue;
    }
    const existing = map.get(product.sectionId);
    if (existing) {
      existing.push(product);
    } else {
      map.set(product.sectionId, [product]);
    }
  }
  return map;
}

export function buildSupermarketCatalog(sections: MenuSection[], products: PublicMenuProduct[]) {
  const productsBySection = groupProductsBySection(products);
  const sectionsById = new Map<string, MenuSection>();
  const childrenByParentId = new Map<string, MenuSection[]>();

  for (const currentSection of sections) {
    sectionsById.set(currentSection.id, currentSection);
    if (currentSection.parentId) {
      const children = childrenByParentId.get(currentSection.parentId);
      if (children) {
        children.push(currentSection);
      } else {
        childrenByParentId.set(currentSection.parentId, [currentSection]);
      }
    }
  }

  const categories: SupermarketCategory[] = [];

  for (const currentSection of sections) {
    if (currentSection.parentId !== null) {
      continue;
    }

    const subcategories = (childrenByParentId.get(currentSection.id) ?? []).map((childSection) => ({
      section: childSection,
      products: productsBySection.get(childSection.id) ?? [],
    }));
    const directProducts = productsBySection.get(currentSection.id) ?? [];
    const productCount = subcategories.reduce(
      (count, subcategory) => count + subcategory.products.length,
      directProducts.length,
    );

    categories.push({
      directProducts,
      productCount,
      section: currentSection,
      subcategories,
    });
  }

  for (const currentSection of sections) {
    if (currentSection.parentId === null || sectionsById.has(currentSection.parentId)) {
      continue;
    }

    const directProducts = productsBySection.get(currentSection.id) ?? [];
    categories.push({
      directProducts,
      productCount: directProducts.length,
      section: currentSection,
      subcategories: [],
    });
  }

  return categories;
}

const SECTION_ICONS: Record<string, typeof UtensilsCrossed> = {
  starters: Salad,
  entradas: Salad,
  mains: UtensilsCrossed,
  principales: UtensilsCrossed,
  desserts: IceCreamCone,
  postres: IceCreamCone,
  drinks: GlassWater,
  bebidas: GlassWater,
};

function getSectionIcon(slug: string) {
  const normalised = slug.toLowerCase();
  for (const [key, Icon] of Object.entries(SECTION_ICONS)) {
    if (normalised.includes(key)) {
      return Icon;
    }
  }
  return Circle;
}

function ProductPrice({
  product,
  currency,
  align = "end",
}: {
  product: PublicMenuProduct;
  currency: string | null;
  align?: "end" | "start";
}) {
  const discountedPrice = product.discountedPrice;
  const hasPromotion =
    product.discountPercentage !== null &&
    product.discountPercentage > 0 &&
    discountedPrice !== null;

  return (
    <div
      className={cn(
        "flex shrink-0 flex-col gap-0.5",
        align === "end" ? "items-end" : "items-start",
      )}
    >
      {hasPromotion ? (
        <>
          <span className="text-muted-foreground text-xs line-through tabular-nums">
            {formatPrice(product.originalPrice, currency)}
          </span>
          <span className="font-semibold text-primary tabular-nums">
            {formatPrice(discountedPrice, currency)}
          </span>
        </>
      ) : (
        <>
          <span className="font-semibold text-primary tabular-nums">
            {formatPrice(product.originalPrice, currency)}
          </span>
          <span aria-hidden className="invisible text-xs">
            &nbsp;
          </span>
        </>
      )}
    </div>
  );
}

function ProductCard({
  product,
  currency,
  canOrder,
  quantity,
  onAdd,
  onDecrement,
}: {
  product: PublicMenuProduct;
  currency: string | null;
  canOrder: boolean;
  quantity: number;
  onAdd: (product: PublicMenuProduct) => void;
  onDecrement: (productId: string) => void;
}) {
  const { t } = useTranslation();
  const hasPromotion =
    product.discountPercentage !== null &&
    product.discountPercentage > 0 &&
    product.discountedPrice !== null;
  const isUnavailable = !product.available;
  const inCart = quantity > 0;

  return (
    <motion.article
      animate={
        inCart ? { boxShadow: "0 0 0 1px var(--primary)" } : { boxShadow: "0 0 0 0 transparent" }
      }
      className={`group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm ${isUnavailable ? "opacity-50" : ""}`}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className="relative">
        {product.imageKey ? (
          <img
            alt={product.name}
            className="aspect-[4/3] w-full object-cover"
            height={300}
            loading="lazy"
            src={`${UPLOADTHING_BASE_URL}/${product.imageKey}`}
            width={400}
          />
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center bg-muted">
            <UtensilsCrossed className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        {hasPromotion ? (
          <span className="absolute top-3 left-3 rounded-lg bg-destructive px-2.5 py-1 font-semibold text-destructive-foreground text-xs shadow-sm">
            -{product.discountPercentage}%
          </span>
        ) : null}
        {isUnavailable ? (
          <span className="absolute top-3 right-3 rounded-lg bg-muted px-2.5 py-1 font-semibold text-muted-foreground text-xs shadow-sm">
            {t("menu.public.unavailable", { defaultValue: "Sin stock" })}
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="line-clamp-2 font-semibold text-lg leading-snug tracking-tight">
            {product.name}
          </h3>
          <ProductPrice currency={currency} product={product} />
        </div>
        <p className="line-clamp-2 min-h-[2lh] text-muted-foreground text-base leading-relaxed">
          {product.description}
        </p>
        {canOrder && !isUnavailable ? (
          <AnimatePresence initial={false} mode="wait">
            {inCart ? (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="mt-auto flex items-center justify-between gap-2"
                exit={{ opacity: 0, y: -4 }}
                initial={{ opacity: 0, y: 4 }}
                key="qty"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <Button
                  aria-label={
                    quantity === 1
                      ? t("menu.public.cart.remove", {
                          defaultValue: "Quitar",
                        })
                      : t("menu.public.cart.decrement", {
                          defaultValue: "Restar",
                        })
                  }
                  onClick={() => onDecrement(product.id)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  {quantity === 1 ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </Button>
                <div className="relative flex h-9 flex-1 items-center justify-center overflow-hidden">
                  <AnimatePresence initial={false} mode="popLayout">
                    <motion.span
                      animate={{ opacity: 1, y: 0 }}
                      className="font-semibold text-base tabular-nums"
                      exit={{ opacity: 0, y: -10 }}
                      initial={{ opacity: 0, y: 10 }}
                      key={quantity}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      {quantity}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <Button
                  aria-label={t("menu.public.cart.increment", {
                    defaultValue: "Sumar",
                  })}
                  onClick={() => onAdd(product)}
                  size="icon"
                  type="button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </motion.div>
            ) : (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="mt-auto"
                exit={{ opacity: 0, y: -4 }}
                initial={{ opacity: 0, y: 4 }}
                key="add"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <Button
                  className="w-full"
                  onClick={() => onAdd(product)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {t("menu.public.cart.add", { defaultValue: "Agregar" })}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        ) : null}
      </div>
    </motion.article>
  );
}

function CategoryNav({
  sections,
  activeId,
  onSelect,
}: {
  sections: MenuSection[];
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const scrollRef = useRef<HTMLElement>(null);

  return (
    <nav className="scrollbar-none flex gap-2 overflow-x-auto pb-2" ref={scrollRef}>
      {sections.map((section) => {
        const Icon = getSectionIcon(section.slug);
        const isActive = activeId === section.id;
        return (
          <button
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 font-medium text-sm transition-all ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent"
            }`}
            key={section.id}
            onClick={() => onSelect(section.id)}
            type="button"
          >
            <Icon className="h-4 w-4" />
            {section.name}
          </button>
        );
      })}
    </nav>
  );
}

function SectionBlock({
  section,
  products,
  currency,
  canOrder,
  quantities,
  onAdd,
  onDecrement,
}: {
  section: MenuSection;
  products: PublicMenuProduct[];
  currency: string | null;
  canOrder: boolean;
  quantities: Map<string, number>;
  onAdd: (product: PublicMenuProduct) => void;
  onDecrement: (productId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="scroll-mt-24 space-y-6" id={`section-${section.id}`}>
      <div className="space-y-1">
        <h2 className="font-semibold font-serif text-2xl tracking-tight">{section.name}</h2>
        {section.description ? (
          <p className="max-w-prose text-muted-foreground italic leading-relaxed">
            {section.description}
          </p>
        ) : null}
      </div>
      {products.length > 0 ? (
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <ProductCard
              canOrder={canOrder}
              currency={currency}
              key={product.id}
              onAdd={onAdd}
              onDecrement={onDecrement}
              product={product}
              quantity={quantities.get(product.id) ?? 0}
            />
          ))}
        </div>
      ) : (
        <p className="py-8 text-center text-muted-foreground text-sm italic">
          {t("menu.public.noProducts")}
        </p>
      )}
    </section>
  );
}

function SupermarketProductRow({
  title,
  description,
  products,
  currency,
  canOrder,
  quantities,
  onAdd,
  onDecrement,
}: {
  title: string;
  description?: string | null;
  products: PublicMenuProduct[];
  currency: string | null;
  canOrder: boolean;
  quantities: Map<string, number>;
  onAdd: (product: PublicMenuProduct) => void;
  onDecrement: (productId: string) => void;
}) {
  const previewProducts = products.slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="font-semibold text-lg tracking-tight">{title}</h3>
        {description ? (
          <p className="max-w-2xl text-muted-foreground text-sm leading-relaxed">{description}</p>
        ) : null}
      </div>
      <div className="-mx-4 overflow-x-auto overscroll-x-contain px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0">
        <div className="flex snap-x snap-mandatory gap-4">
          {previewProducts.map((product) => (
            <div className="w-44 shrink-0 snap-start sm:w-56" key={product.id}>
              <ProductCard
                canOrder={canOrder}
                currency={currency}
                onAdd={onAdd}
                onDecrement={onDecrement}
                product={product}
                quantity={quantities.get(product.id) ?? 0}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SupermarketCategoryBlock({
  category,
  currency,
  canOrder,
  quantities,
  onAdd,
  onDecrement,
}: {
  category: SupermarketCategory;
  currency: string | null;
  canOrder: boolean;
  quantities: Map<string, number>;
  onAdd: (product: PublicMenuProduct) => void;
  onDecrement: (productId: string) => void;
}) {
  const { t } = useTranslation();
  const visibleSubcategories = category.subcategories.filter(
    (subcategory) => subcategory.products.length > 0,
  );

  return (
    <section className="scroll-mt-24 space-y-6" id={`section-${category.section.id}`}>
      <div className="space-y-1">
        <h2 className="font-semibold font-serif text-2xl tracking-tight">
          {category.section.name}
        </h2>
        {category.section.description ? (
          <p className="max-w-prose text-muted-foreground italic leading-relaxed">
            {category.section.description}
          </p>
        ) : null}
      </div>

      <div className="space-y-8">
        {category.directProducts.length > 0 ? (
          <SupermarketProductRow
            canOrder={canOrder}
            currency={currency}
            onAdd={onAdd}
            onDecrement={onDecrement}
            products={category.directProducts}
            quantities={quantities}
            title={t("menu.public.catalog.allCategory", {
              defaultValue: "Toda la categoría",
            })}
          />
        ) : null}

        {visibleSubcategories.map((subcategory) => (
          <SupermarketProductRow
            canOrder={canOrder}
            currency={currency}
            description={subcategory.section.description}
            key={subcategory.section.id}
            onAdd={onAdd}
            onDecrement={onDecrement}
            products={subcategory.products}
            quantities={quantities}
            title={subcategory.section.name}
          />
        ))}
      </div>
    </section>
  );
}

function CartDrawer({
  orgSlug,
  currency,
  canOrder,
  businessType,
}: {
  orgSlug: string;
  currency: string | null;
  canOrder: boolean;
  businessType: PublicBusinessType;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const cart = useCart(orgSlug);
  const isSupermarket = businessType === "supermarket";

  if (!canOrder || cart.itemCount === 0) {
    return null;
  }

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <motion.div
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="fixed right-4 bottom-6 z-20 sm:right-8"
        initial={{ opacity: 0, y: 24, scale: 0.9 }}
        transition={{ type: "spring", stiffness: 320, damping: 24 }}
      >
        <SheetTrigger asChild>
          <Button className="shadow-lg" size="lg" type="button">
            <ShoppingCart className="mr-2 h-5 w-5" />
            <motion.span
              animate={{ scale: [1, 1.12, 1] }}
              className="inline-flex items-baseline gap-1 tabular-nums"
              key={cart.itemCount}
              transition={{ duration: 0.32, ease: "easeOut" }}
            >
              {cart.itemCount} · {formatPrice(cart.subtotal, currency)}
            </motion.span>
          </Button>
        </SheetTrigger>
      </motion.div>
      <SheetContent className="flex w-full flex-col sm:max-w-md px-5">
        <SheetHeader>
          <SheetTitle className="text-2xl">
            {isSupermarket
              ? t("menu.public.catalog.cart.title", {
                  defaultValue: "Tu carrito",
                })
              : t("menu.public.cart.title", { defaultValue: "Tu pedido" })}
          </SheetTitle>
          <SheetDescription className="text-lg">
            {isSupermarket
              ? t("menu.public.catalog.cart.description", {
                  defaultValue: "Revisá los productos antes de continuar.",
                })
              : t("menu.public.cart.description", {
                  defaultValue: "Revisá los items antes de continuar.",
                })}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {cart.state.lines.map((line) => (
            <div
              className="flex items-center gap-3 rounded-lg border border-border p-3"
              key={line.productId}
            >
              <div className="flex-1">
                <div className="font-medium text-sm">{line.name}</div>
                <div className="text-muted-foreground text-xs">
                  {formatPrice(line.unitPrice, currency)} ·{" "}
                  {formatPrice(line.unitPrice * line.quantity, currency)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  aria-label="decrement"
                  onClick={() => cart.setQuantity(line.productId, line.quantity - 1)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-6 text-center font-medium text-sm tabular-nums">
                  {line.quantity}
                </span>
                <Button
                  aria-label="increment"
                  onClick={() => cart.setQuantity(line.productId, line.quantity + 1)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <Plus className="h-3 w-3" />
                </Button>
                <Button
                  aria-label="remove"
                  onClick={() => cart.removeLine(line.productId)}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <SheetFooter className="flex-col gap-2 sm:flex-col">
          <div className="flex items-center justify-between font-semibold">
            <span>{t("menu.public.cart.subtotal", { defaultValue: "Subtotal" })}</span>
            <span className="tabular-nums">{formatPrice(cart.subtotal, currency)}</span>
          </div>
          <Button
            className="w-full"
            onClick={() => {
              setOpen(false);
              navigate(`/m/${orgSlug}/checkout`);
            }}
            size="lg"
            type="button"
          >
            {isSupermarket
              ? t("menu.public.catalog.cart.checkout", {
                  defaultValue: "Ir al checkout",
                })
              : t("menu.public.cart.checkout", { defaultValue: "Ir a pagar" })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export function RestaurantPublicMenu({
  data,
  orgSlug,
  guest,
  qrToken,
  canOrder,
  quantitiesByProductId,
  onAdd,
  onDecrement,
}: MenuContentProps) {
  const { t } = useTranslation();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const productsBySection = useMemo(() => groupProductsBySection(data.products), [data.products]);
  const visibleSections = data.sections.filter(
    (s) => productsBySection.has(s.id) && (productsBySection.get(s.id)?.length ?? 0) > 0,
  );

  const handleSectionSelect = (id: string) => {
    setActiveSectionId(id);
    document
      .getElementById(`section-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-12 sm:px-8"
      id="public-menu-content"
    >
      <header className="space-y-4 pb-8">
        <div className="flex items-center gap-2">
          <Badge
            className="rounded-full font-mono text-xs uppercase tracking-widest"
            variant="secondary"
          >
            {t("menu.public.menuLabel", { defaultValue: "Menu" })}
          </Badge>
          {guest.tableLabel ? (
            <Badge className="rounded-full text-xs" variant="default">
              {guest.tableLabel}
            </Badge>
          ) : null}
        </div>
        <h1 className="font-bold font-serif text-4xl tracking-tight sm:text-5xl">
          {data.organization.name}
        </h1>
        {guest.error ? <p className="text-destructive text-sm">{guest.error}</p> : null}
        {data.ordering?.enabled && !guest.tableLabel && !qrToken ? (
          <p className="text-muted-foreground text-sm">
            {t("menu.public.scanToOrder", {
              defaultValue: "Escaneá el QR de tu mesa para hacer un pedido.",
            })}
          </p>
        ) : null}
      </header>

      {visibleSections.length > 1 ? (
        <div className="sticky top-0 z-10 -mx-4 bg-background/85 px-4 py-3 backdrop-blur-lg sm:-mx-8 sm:px-8">
          <CategoryNav
            activeId={activeSectionId}
            onSelect={handleSectionSelect}
            sections={visibleSections}
          />
        </div>
      ) : null}

      {data.sections.length > 0 ? (
        <div className="space-y-16 pt-8">
          {data.sections.map((section) => (
            <SectionBlock
              canOrder={canOrder}
              currency={data.organization.currency}
              key={section.id}
              onAdd={onAdd}
              onDecrement={onDecrement}
              products={productsBySection.get(section.id) ?? []}
              quantities={quantitiesByProductId}
              section={section}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center py-20">
          <div className="space-y-3 text-center">
            <Bookmark className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <h2 className="font-semibold font-serif text-xl">{t("menu.public.emptyTitle")}</h2>
            <p className="text-muted-foreground text-sm">{t("menu.public.emptyDescription")}</p>
          </div>
        </div>
      )}

      {orgSlug ? (
        <CartDrawer
          businessType={data.organization.businessType}
          canOrder={canOrder}
          currency={data.organization.currency}
          orgSlug={orgSlug}
        />
      ) : null}
    </div>
  );
}

export function SupermarketPublicCatalog({
  data,
  orgSlug,
  guest,
  canOrder,
  quantitiesByProductId,
  onAdd,
  onDecrement,
}: MenuContentProps) {
  const { t } = useTranslation();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const categories = useMemo(
    () => buildSupermarketCatalog(data.sections, data.products),
    [data.products, data.sections],
  );
  const visibleCategories = categories.filter((category) => category.productCount > 0);
  const visibleSections = visibleCategories.map((category) => category.section);

  const handleSectionSelect = (id: string) => {
    setActiveSectionId(id);
    document
      .getElementById(`section-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-12 sm:px-8"
      id="public-menu-content"
    >
      <header className="space-y-4 pb-8">
        <div className="flex items-center gap-2">
          <Badge
            className="rounded-full font-mono text-xs uppercase tracking-widest"
            variant="secondary"
          >
            {t("menu.public.catalog.label", { defaultValue: "Catálogo" })}
          </Badge>
        </div>
        <h1 className="font-bold font-serif text-4xl tracking-tight sm:text-5xl">
          {data.organization.name}
        </h1>
        {guest.error ? <p className="text-destructive text-sm">{guest.error}</p> : null}
      </header>

      {visibleSections.length > 1 ? (
        <div className="sticky top-0 z-10 -mx-4 bg-background/85 px-4 py-3 backdrop-blur-lg sm:-mx-8 sm:px-8">
          <CategoryNav
            activeId={activeSectionId}
            onSelect={handleSectionSelect}
            sections={visibleSections}
          />
        </div>
      ) : null}

      {visibleCategories.length > 0 ? (
        <div className="space-y-16 pt-8">
          {visibleCategories.map((category) => (
            <SupermarketCategoryBlock
              canOrder={canOrder}
              category={category}
              currency={data.organization.currency}
              key={category.section.id}
              onAdd={onAdd}
              onDecrement={onDecrement}
              quantities={quantitiesByProductId}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center py-20">
          <div className="space-y-3 text-center">
            <PackageOpen className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <h2 className="font-semibold font-serif text-xl">
              {t("menu.public.catalog.emptyTitle", {
                defaultValue: "Este catálogo todavía se está preparando",
              })}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("menu.public.catalog.emptyDescription", {
                defaultValue: "Todavía no hay categorías visibles.",
              })}
            </p>
          </div>
        </div>
      )}

      {orgSlug ? (
        <CartDrawer
          businessType={data.organization.businessType}
          canOrder={canOrder}
          currency={data.organization.currency}
          orgSlug={orgSlug}
        />
      ) : null}
    </div>
  );
}

export default function PublicMenuPage() {
  const { t } = useTranslation();
  const { orgSlug } = useParams();
  const [searchParams] = useSearchParams();
  const qrToken = searchParams.get("t");
  const guest = useGuestSession(orgSlug, qrToken);
  const { data, error, isLoading } = usePublicMenu(orgSlug);
  const { setTheme, setColorTheme } = useTheme();
  const cart = useCart(orgSlug);

  useEffect(() => {
    if (!data?.organization) {
      return;
    }
    const { theme, colorTheme } = data.organization;
    if (theme) {
      setTheme(theme as "dark" | "light" | "system");
    }
    if (colorTheme) {
      setColorTheme(colorTheme);
    }
  }, [data?.organization, setTheme, setColorTheme]);

  const quantitiesByProductId = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of cart.state.lines) {
      map.set(line.productId, line.quantity);
    }
    return map;
  }, [cart.state.lines]);

  const handleAdd = (product: PublicMenuProduct) => {
    cart.addLine({
      productId: product.id,
      name: product.name,
      originalPrice: product.originalPrice,
      unitPrice: product.discountedPrice !== null ? product.discountedPrice : product.originalPrice,
    });
  };

  const handleDecrement = (productId: string) => {
    const current = quantitiesByProductId.get(productId) ?? 0;
    cart.setQuantity(productId, current - 1);
  };

  if (isLoading) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-8 px-4 py-12 sm:px-8">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-64" />
        <div className="flex gap-3">
          <Skeleton className="h-10 w-28 rounded-full" />
          <Skeleton className="h-10 w-28 rounded-full" />
          <Skeleton className="h-10 w-28 rounded-full" />
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center px-4 py-12 sm:px-8">
        <div className="space-y-3 text-center">
          <h1 className="font-semibold font-serif text-2xl">{t("menu.public.title")}</h1>
          <p className="text-muted-foreground">{error?.message || t("menu.public.loadError")}</p>
        </div>
      </div>
    );
  }

  const canOrder = Boolean(
    data.ordering?.enabled &&
    (data.organization.businessType === "supermarket" || guest.tableLabel) &&
    !guest.error,
  );

  return (
    <>
      {data.organization.businessType === "supermarket" ? (
        <SupermarketPublicCatalog
          canOrder={canOrder}
          data={data}
          guest={guest}
          onAdd={handleAdd}
          onDecrement={handleDecrement}
          orgSlug={orgSlug}
          qrToken={qrToken}
          quantitiesByProductId={quantitiesByProductId}
        />
      ) : (
        <RestaurantPublicMenu
          canOrder={canOrder}
          data={data}
          guest={guest}
          onAdd={handleAdd}
          onDecrement={handleDecrement}
          orgSlug={orgSlug}
          qrToken={qrToken}
          quantitiesByProductId={quantitiesByProductId}
        />
      )}
      <AdminInvitationModal />
    </>
  );
}
